<script setup lang="ts">
import { mdiArrowLeft } from '@mdi/js'
// DESIGN.md section 9.1, Players screen sub-screen — Player profile.
//
// 2026-08-31 round-4 feedback item 2 — reworked lichess-style:
//  - header styled like a WhatsApp chat header (back button, profile pic, name, subtitle
//    "elo · rank" for the selected pool) instead of the old plain title block.
//  - stat tiles: total matches played / won / win rate, summed across BOTH pools (a player's
//    overall record) — computed client-side from `ratings.rapid`/`ratings.classical`, no new
//    backend field needed for this part.
//  - a real elo-over-time line chart (EloChart.vue, hand-rolled SVG, no library) replaces the
//    old tiny Sparkline — same `ratingHistory` data GET /api/players/{id} already returned
//    (rating_history table, one row per finished match per pool), just charted bigger.
//  - "Top rivals": most-played opponents overall, sorted by matches together desc — a NEW
//    `topRivals` field on this endpoint's response (server/app/services/player_service.py's
//    get_top_rivals; DESIGN.md section 7.2).
//  - recent matches: unchanged.
import { computed, onMounted, ref, watch } from 'vue'
import Avatar from '../components/Avatar.vue'
import EloChart from '../components/EloChart.vue'
import { getPlayer, getVoiceProfiles } from '../backend'
import { stt } from '../stt'
import { settings } from '../store/settings'
import { nav, navigate } from '../store/nav'
import type { PlayerDetail, RatingPool } from '../types'

const player = ref<PlayerDetail | null>(null)
const loadError = ref(false)
const pool = ref<RatingPool>('rapid')

async function load() {
  const id = nav.params.playerId
  if (id == null) {
    loadError.value = true
    return
  }
  loadError.value = false
  player.value = null
  try {
    player.value = await getPlayer(id)
  } catch {
    loadError.value = true
  }
}

onMounted(load)
watch(() => nav.params.playerId, load)

function goBack() {
  navigate('players')
}

function openPlayer(id: number) {
  navigate('player', { playerId: id })
}

const headerRating = computed(() => {
  const r = player.value?.ratings[pool.value]
  if (!r) return '—'
  return `${r.rating}${r.provisional ? '?' : ''}`
})

const headerRank = computed(() => {
  const rank = player.value?.rank[pool.value]
  return rank != null ? `Rank #${rank}` : 'Unranked'
})

// Total record across BOTH pools — a player's overall "how am I doing", independent of the
// Rapid/Classic toggle below (which only affects the elo chart + header subtitle).
const totalStats = computed(() => {
  const p = player.value
  if (!p) return { matches: 0, wins: 0, winRate: 0 }
  const matches = p.ratings.rapid.matches + p.ratings.classical.matches
  const wins = p.ratings.rapid.wins + p.ratings.classical.wins
  return { matches, wins, winRate: matches > 0 ? wins / matches : 0 }
})

function chartPoints(forPool: RatingPool): { at: string; value: number }[] {
  const hist = player.value?.ratingHistory[forPool] ?? []
  if (hist.length === 0) return []
  return [{ at: hist[0].at, value: hist[0].before }, ...hist.map((h) => ({ at: h.at, value: h.after }))]
}

// Round-15 (2026-08-31): optional "Train my voice" — while a session runs, every utterance
// kmb-stt finalizes is stored LABELED with this player's id (audio + x-vector) and folded into
// their voice_profiles centroid, which is what lets "gano yo"/"punto mío" be attributed to the
// right side during a live match. Passive/unlabeled corpus collection continues regardless
// (src/stt.ts); this is purely the labeling surface.
const voiceSampleCount = ref<number | null>(null)

async function loadVoiceProfile() {
  try {
    const res = await getVoiceProfiles()
    voiceSampleCount.value = res.items.find((v) => v.playerId === nav.params.playerId)?.sampleCount ?? 0
  } catch {
    voiceSampleCount.value = null
  }
}
onMounted(loadVoiceProfile)
watch(() => nav.params.playerId, loadVoiceProfile)

// Round-16 (2026-08-31): the actual training flow moved to its own page (VoiceTraining.vue —
// realtime level, live transcript, guided reading paragraph, progress bar). This screen keeps
// only the "on file" status + the entry point; reloading the sample count when returning here
// (watch below) covers a training session that ran and came back.
watch(() => nav.screen, (screen) => {
  if (screen === 'player') void loadVoiceProfile()
})

function opponentOf(m: PlayerDetail['recentMatches'][number]) {
  return m.players.left.id === player.value?.id ? m.players.right : m.players.left
}

function scoreLine(m: PlayerDetail['recentMatches'][number]) {
  if (!player.value) return ''
  const mine = m.gamesWon[player.value.id] ?? 0
  const opp = m.gamesWon[opponentOf(m).id] ?? 0
  return `${mine}–${opp}`
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col overflow-y-auto">
    <div v-if="loadError" class="p-4 text-neutral-500">Could not load this player</div>

    <template v-else-if="player">
      <!-- WhatsApp-style chat header: back, avatar, name + "elo · rank" subtitle. -->
      <div class="sticky top-0 z-10 flex items-center gap-3 px-3 py-2.5 bg-neutral-950/95 border-b border-neutral-800 backdrop-blur">
        <button
          type="button"
          class="tap-target w-10 h-10 min-h-0 min-w-0 shrink-0 rounded-full active:bg-neutral-800 flex items-center justify-center text-neutral-400"
          aria-label="Back to Players"
          @click="goBack"
        >
          <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor"><path :d="mdiArrowLeft" /></svg>
        </button>
        <Avatar :player="player" :size="44" />
        <div class="flex-1 min-w-0">
          <div class="font-bold truncate leading-tight">
            {{ player.displayName }}
            <span v-if="player.isGuest" class="ml-1 text-xs font-normal text-neutral-500">guest</span>
          </div>
          <div class="text-sm text-neutral-500 font-mono truncate">{{ headerRating }} {{ pool === 'rapid' ? 'rapid' : 'classical' }} · {{ headerRank }}</div>
        </div>
      </div>

      <div class="flex flex-col gap-4 p-4">
        <div class="flex gap-2">
          <button
            type="button"
            class="tap-target px-5 rounded-xl font-semibold"
            :class="pool === 'rapid' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="pool = 'rapid'"
          >
            Rapid
          </button>
          <button
            type="button"
            class="tap-target px-5 rounded-xl font-semibold"
            :class="pool === 'classical' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
            @click="pool = 'classical'"
          >
            Classic
          </button>
        </div>

        <div class="grid grid-cols-3 gap-2">
          <div class="rounded-xl bg-neutral-900 border border-neutral-800 p-3 flex flex-col items-center gap-1">
            <span class="text-xl font-mono font-black">{{ totalStats.matches }}</span>
            <span class="text-[10px] font-bold text-neutral-500 uppercase tracking-wide">Matches</span>
          </div>
          <div class="rounded-xl bg-neutral-900 border border-neutral-800 p-3 flex flex-col items-center gap-1">
            <span class="text-xl font-mono font-black">{{ totalStats.wins }}</span>
            <span class="text-[10px] font-bold text-neutral-500 uppercase tracking-wide">Won</span>
          </div>
          <div class="rounded-xl bg-neutral-900 border border-neutral-800 p-3 flex flex-col items-center gap-1">
            <span class="text-xl font-mono font-black">{{ Math.round(totalStats.winRate * 100) }}%</span>
            <span class="text-[10px] font-bold text-neutral-500 uppercase tracking-wide">Win rate</span>
          </div>
        </div>

        <div class="rounded-2xl bg-neutral-900 border border-neutral-800 p-4 flex flex-col gap-2">
          <span class="text-sm font-bold text-neutral-400 uppercase tracking-wide">{{ pool === 'rapid' ? 'Rapid' : 'Classic' }} rating</span>
          <EloChart :points="chartPoints(pool)" />
        </div>

        <!-- Round-15: optional voice training. Only shown when voice commands are enabled. -->
        <div v-if="settings.voiceCommandsEnabled" class="rounded-2xl bg-neutral-900 border border-neutral-800 p-4 flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <span class="text-sm font-bold text-neutral-400 uppercase tracking-wide flex-1">Voice</span>
            <span class="w-2 h-2 rounded-full" :class="stt.connected ? 'bg-emerald-400' : 'bg-neutral-600'" :title="stt.connected ? 'Mic connected' : 'Mic offline'"></span>
          </div>
          <p class="text-xs text-neutral-500">
            {{ voiceSampleCount ? `${voiceSampleCount} voice sample${voiceSampleCount === 1 ? '' : 's'} on file.` : 'No voice samples yet.' }}
            Training lets the table attribute "gano yo" / "punto mío" to you by voice — and claim a
            corner by saying "yo juego en el campo/lado rojo/azul" or "estoy en el campo/lado
            rojo/azul" (recommended, ~2.5s gives a better voice match than the short "soy
            rojo"/"soy azul"/"soy del equipo rojo").
          </p>
          <button
            type="button"
            class="tap-target rounded-xl font-semibold bg-emerald-600 active:bg-emerald-700 disabled:opacity-40"
            :disabled="!stt.connected"
            @click="navigate('voiceTraining', { playerId: player.id })"
          >
            Train my voice →
          </button>
        </div>

        <div v-if="player.topRivals.length" class="flex flex-col gap-2">
          <h2 class="text-sm font-bold text-neutral-400 uppercase tracking-wide">Top rivals</h2>
          <button
            v-for="rival in player.topRivals"
            :key="rival.player.id"
            type="button"
            class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-900 border border-neutral-800 px-3 py-2 text-left active:bg-neutral-800"
            @click="openPlayer(rival.player.id)"
          >
            <Avatar :player="rival.player" :size="36" />
            <span class="flex-1 font-semibold truncate">{{ rival.player.displayName }}</span>
            <span class="font-mono" :class="rival.wins >= rival.losses ? 'text-emerald-400' : 'text-neutral-400'">{{ rival.wins }}–{{ rival.losses }}</span>
            <span class="text-xs text-neutral-500 w-20 text-right">{{ rival.matches }} match{{ rival.matches === 1 ? '' : 'es' }}</span>
          </button>
        </div>

        <div class="flex flex-col gap-2">
          <h2 class="text-sm font-bold text-neutral-400 uppercase tracking-wide">Recent matches</h2>
          <button
            v-for="m in player.recentMatches"
            :key="m.id"
            type="button"
            class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-900 border border-neutral-800 px-3 py-2 text-left active:bg-neutral-800"
            @click="navigate('pastMatchDetail', { matchId: m.id })"
          >
            <Avatar :player="opponentOf(m)" :size="32" />
            <span class="flex-1 font-semibold truncate">vs {{ opponentOf(m).displayName }}</span>
            <span class="font-mono" :class="m.winnerPlayerId === player.id ? 'text-emerald-400' : 'text-neutral-400'">{{ scoreLine(m) }}</span>
            <span class="text-xs text-neutral-500">{{ m.matchType.name }}</span>
          </button>
          <div v-if="player.recentMatches.length === 0" class="text-neutral-500">No matches yet</div>
        </div>
      </div>
    </template>
  </div>
</template>
